import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, nextTick, onMounted, ref, watch, withDefaults } from 'vue';
import CombinationsConfiguratorCanvas from '@/modules/ConfiguratorCombinations/components/CombinationsConfiguratorCanvas.vue';
import CombinationsConfiguratorCanvasHero from '@/modules/ConfiguratorCombinations/components/CombinationsConfiguratorCanvasHero.vue';
import CombinationsWarning from '@/modules/ConfiguratorCombinations/components/CombinationsWarning.vue';
import { WindowTemplate } from '@/interfaces/components/combinations/WindowTemplate';
import { WindowElement } from '@/interfaces/components/combinations/WindowElement';
import {
    CombinationsConfiguratorFormData,
    DynamicCanvasSizes,
} from '@/modules/ConfiguratorCombinations/interfaces/CombinationsInterfaces';
import { GlobalOptions } from '@/enums/global/GlobalOptions';
import {
    sortWindowElementsByPosition,
    downloadStageAsPng,
} from '@/modules/ConfiguratorCombinations/helpers/CombinationsHelper';

interface Props {
    productFormData: CombinationsConfiguratorFormData[] | null;
    selectedRows: number[];
    guideType: string;
    secondGuideType: string;
    combinationType: string;
    isLastStep: boolean;
    widthDifference: number;
    combinedWidth: number;
    currentStep: number;
    currentConfig: any;
    canvasTemplateConfiguration: DynamicCanvasSizes;
    configuratorType?: string;
    disableDownloadButton?: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CombinationsConfiguratorTemplate',
  props: {
    productFormData: null,
    selectedRows: null,
    guideType: null,
    secondGuideType: null,
    combinationType: null,
    isLastStep: { type: Boolean },
    widthDifference: null,
    combinedWidth: null,
    currentStep: null,
    currentConfig: null,
    canvasTemplateConfiguration: null,
    configuratorType: { default: 'standard' },
    disableDownloadButton: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as { productFormData: CombinationsConfiguratorFormData[] | null, selectedRows: number[], guideType: string, secondGuideType: string, combinationType: string, isLastStep: boolean, widthDifference: number, combinedWidth: number, currentStep: number, currentConfig: any, canvasTemplateConfiguration: DynamicCanvasSizes, configuratorType: string, disableDownloadButton?: boolean };



const canvasComponent = computed(() =>
    props.configuratorType?.includes('hero') ? CombinationsConfiguratorCanvasHero : CombinationsConfiguratorCanvas,
);

const { proxy } = getCurrentInstance()!;

const extraSpaceForLabels = 400;
const percentageIncreasement = 2.5;
const dividerForScaleAdjustmentBasis = 0.2; // Less value means less space between windows
const extraWidthPaddingScale = 0.3;

const selectedTemplate = ref<any>(null);
const stageRef = ref<any>(null);

function onDownloadCanvas() {
    const stageInstance = stageRef.value?.getNode?.() ?? stageRef.value?.getStage?.();
    if (!stageInstance) {
        return;
    }
    downloadStageAsPng(stageInstance, 'combined-box.png');
}

const windowTemplates = ref<WindowTemplate[]>([
    {
        id: 'template-2',
        stageConfig: { height: 0, width: 0, x: 50, y: 0 },
        windowElements: [
            {
                id: 'window-1',
                canvasConfig: {
                    width: 0,
                    height: 0,
                    positionX: null,
                    positionY: null,
                    initialPositionX: null,
                    initialPositionY: 35,
                    position: 1,
                    guideType: '',
                },
            },
            {
                id: 'window-2',
                canvasConfig: {
                    width: 0,
                    height: 0,
                    positionX: null,
                    positionY: null,
                    initialPositionX: null,
                    initialPositionY: 35,
                    position: 2,
                    guideType: '',
                },
            },
            {
                id: 'window-3',
                canvasConfig: {
                    width: 0,
                    height: 0,
                    positionX: null,
                    positionY: null,
                    initialPositionX: null,
                    initialPositionY: 35,
                    position: 3,
                    guideType: '',
                },
            },
        ],
    },
]);

const dividerForScaleAdjustment = computed(() => {
    // Dynamically calculate the divider based on the combined width
    // Base value of 0.2 works well for widths around 200-250
    const baseWidth = 200;
    const baseValue = dividerForScaleAdjustmentBasis;
    const increment = 0.001;
    const incrementStep = 45;

    if (props.combinedWidth <= baseWidth) {
        return baseValue;
    }

    const stepsAboveBase = Math.floor((props.combinedWidth - baseWidth) / incrementStep);

    return baseValue + stepsAboveBase * increment;
});

const heightScaleFactor = computed(() => {
    const highestWindowHeight = Math.max(
        ...(props.productFormData?.map((row, index) => {
            if (index >= props.selectedRows.length) return 0;
            const heightField = row.formData.find((field) => field.fieldPID === GlobalOptions.HEIGHT_PID);
            return heightField ? parseInt(heightField.fieldValue as string) : 0;
        }) || [0]),
    );

    if (highestWindowHeight <= 100) {
        return 1.0;
    }

    const baseHeight = 100;
    const logBase = 2;
    const standardLogBase = 1;
    const scaleFactor =
        Math.log((baseHeight / highestWindowHeight) * logBase + standardLogBase) / Math.log(logBase + standardLogBase);

    const minScale = 0.4;
    const scale = minScale + scaleFactor * (standardLogBase - minScale);
    return Math.max(scale, minScale);
});

const widthScaleFactor = computed(() => {
    const absWidth = props.combinedWidth;

    if (absWidth <= 200) {
        return 1;
    }

    const baseWidth = 200;
    const logBase = 2;
    const standardLogBase = 1;
    const scaleFactor =
        Math.log((baseWidth / absWidth) * logBase + standardLogBase) / Math.log(logBase + standardLogBase);

    const minScale = 0.2;
    const scale = minScale + scaleFactor * (standardLogBase - minScale);

    return Math.max(scale, minScale);
});

const masterScaleFactor = computed(() => {
    return Math.min(widthScaleFactor.value, heightScaleFactor.value);
});

const layerConfig = computed(() => {
    return {
        y: 30,
        x: 30,
    };
});

function transformDimension(value: string | number) {
    return parseFloat(value as any) * 10;
}

function increasementForWiderWidths(width: string) {
    const medianValue = 250;
    const dividerForUpscaling = 6.76;

    if (+width <= medianValue) {
        return 0;
    }

    const baseDifference = parseFloat(width) - medianValue;

    return Math.round(baseDifference / dividerForUpscaling);
}

function calculateStageConfigHeight(template: WindowTemplate) {
    const height = Math.max(
        ...template.windowElements.map((element) => transformDimension(element.canvasConfig.height) as number),
    );
    const scaleFactor = masterScaleFactor.value === 1 ? 1 : masterScaleFactor.value - 0.1;

    const extraMargin = props.canvasTemplateConfiguration.extraMarginForCanvasStage;
    const heightDivisor = 3.5 / scaleFactor;

    return (height + extraMargin) / heightDivisor;
}

function setElementsInitialPositions(windowElements: WindowElement[]) {
    const sortedElements = sortWindowElementsByPosition(windowElements);

    sortedElements.forEach((element: any, i: number) => {
        if (i === 0) {
            proxy.$set(element.canvasConfig, 'initialPositionX', 50);
            proxy.$set(element.canvasConfig, 'positionX', 50);
        } else {
            const transformedWidth = transformDimension(sortedElements[i - 1]?.canvasConfig.width) as any;
            const transformedXPosition = sortedElements[i - 1]?.canvasConfig.initialPositionX || 0;
            const newPositionX =
                (transformedWidth - increasementForWiderWidths(transformedWidth)) * dividerForScaleAdjustment.value +
                transformedXPosition;

            proxy.$set(element.canvasConfig, 'initialPositionX', newPositionX);
            proxy.$set(element.canvasConfig, 'positionX', newPositionX);
        }

        proxy.$set(element.canvasConfig, 'positionY', element.canvasConfig.initialPositionY);
    });
}

async function setTemplatePositionsAndStageConfig(windowTemplate: WindowTemplate, setStage: boolean = true) {
    const dividerForStageWidth = 4.5 + (windowTemplate.windowElements.length * percentageIncreasement) / 10;
    await nextTick();

    const { width } = windowTemplate.windowElements.reduce(
        (total: any, currentElement: any) => {
            total.width += parseFloat(transformDimension(currentElement.canvasConfig.width) as any);
            total.height += parseFloat(transformDimension(currentElement.canvasConfig.height) as any);

            return total;
        },
        { width: 0, height: 0 },
    );

    const stageConfig = {
        height: calculateStageConfigHeight(windowTemplate),
        width:
            (width * masterScaleFactor.value + extraSpaceForLabels) / dividerForStageWidth +
            props.widthDifference * (masterScaleFactor.value + extraWidthPaddingScale),
        scale: { x: masterScaleFactor.value, y: masterScaleFactor.value },
        x: props.widthDifference / 2,
    };

    if (setStage) {
        proxy.$set(windowTemplate, 'stageConfig', { ...stageConfig });
    }

    setElementsInitialPositions(windowTemplate.windowElements);
}

function setTemplatesPositionsAndStageConfig() {
    windowTemplates.value.forEach(async (windowTemplate: WindowTemplate) => {
        setTemplatePositionsAndStageConfig(windowTemplate);
    });
}

function getCircleLetters(_windowElement: WindowElement, index: number): Array<{ letter: string; visible: boolean }> {
    const drive = props.productFormData?.[index]?.formData.find(
        (field) => field.fieldPID === GlobalOptions.DRIVE_PID,
    )?.fieldValue;

    return [{ letter: drive?.toString() ?? '', visible: !!drive }];
}

function selectWindowTemplate(templateId: string) {
    if (templateId === '') {
        selectedTemplate.value = null;
        return;
    }

    selectedTemplate.value = JSON.parse(
        JSON.stringify(windowTemplates.value.find((template: any) => template.id === templateId)),
    );
}

function setSelectedTemplateDimensions() {
    if (!selectedTemplate.value) return;
    props.productFormData?.forEach((row: CombinationsConfiguratorFormData, index: number) => {
        row.formData.forEach((field: any) => {
            switch (field.fieldPID) {
                case GlobalOptions.WIDTH_PID:
                    selectedTemplate.value.windowElements[index].canvasConfig.width = parseInt(
                        field.fieldValue as string,
                    );
                    break;
                case GlobalOptions.HEIGHT_PID:
                    selectedTemplate.value.windowElements[index].canvasConfig.height = parseInt(
                        field.fieldValue as string,
                    );
                    break;
                case GlobalOptions.ASSEMBLY_POSITION_PID:
                    selectedTemplate.value.windowElements[index].canvasConfig.position = field.fieldValue as string;

                    selectedTemplate.value.windowElements.forEach((element: any) => {
                        element.canvasConfig.positionX = null;
                        element.canvasConfig.positionY = null;
                        element.canvasConfig.initialPositionX = null;
                    });
                    break;
            }
        });
    });

    for (let i = props.selectedRows.length; i < selectedTemplate.value.windowElements.length; i++) {
        selectedTemplate.value.windowElements[i].canvasConfig.width = 0;
        selectedTemplate.value.windowElements[i].canvasConfig.height = 0;
        selectedTemplate.value.windowElements[i].canvasConfig.positionX = null;
        selectedTemplate.value.windowElements[i].canvasConfig.positionY = null;
        selectedTemplate.value.windowElements[i].canvasConfig.initialPositionX = null;
        selectedTemplate.value.windowElements[i].canvasConfig.initialPositionY = 35;
        selectedTemplate.value.windowElements[i].canvasConfig.position = i + 1;
    }
}

function moveWindowsToTop() {
    setTimeout(() => {
        const sortedWindows = [...selectedTemplate.value.windowElements].sort((a, b) => {
            const positionA =
                typeof a.canvasConfig.position === 'string'
                    ? parseInt(a.canvasConfig.position, 10)
                    : a.canvasConfig.position || 0;
            const positionB =
                typeof b.canvasConfig.position === 'string'
                    ? parseInt(b.canvasConfig.position, 10)
                    : b.canvasConfig.position || 0;

            return positionA - positionB;
        });

        for (let i = sortedWindows.length - 1; i >= 0; i--) {
            const windowItem = sortedWindows[i];
            const windowComponent = proxy.$refs[windowItem.id];
            if (windowComponent && Array.isArray(windowComponent) && windowComponent[0]) {
                (windowComponent[0] as any).moveWindowToTop();

                windowItem.canvasConfig.positionX = windowItem.canvasConfig.initialPositionX;
                windowItem.canvasConfig.positionY = windowItem.canvasConfig.initialPositionY;
            }
        }
    }, 0);
}

async function recalculateCanvas() {
    setSelectedTemplateDimensions();
    await nextTick();

    setTemplatePositionsAndStageConfig(selectedTemplate.value);

    await nextTick();
    moveWindowsToTop();
}

const sortedWindowElements = computed(() => {
    if (!selectedTemplate.value || !selectedTemplate.value.windowElements) return [];

    const elements = selectedTemplate.value.windowElements.slice(0, props.selectedRows.length);

    return sortWindowElementsByPosition(elements);
});

watch(
    [() => props.productFormData, () => props.guideType, () => props.secondGuideType],
    async () => {
        if (!props.productFormData) return;
        await recalculateCanvas();
    },
    { deep: true },
);

watch(
    () => props.widthDifference,
    async () => {
        await recalculateCanvas();
    },
);

watch(
    () => props.canvasTemplateConfiguration,
    async () => {
        await recalculateCanvas();
    },
    { deep: true },
);

onMounted(async () => {
    setTemplatesPositionsAndStageConfig();
    await nextTick();
    selectWindowTemplate('template-2');
    await nextTick();
    await recalculateCanvas();
});

return { __sfc: true,props, canvasComponent, proxy, extraSpaceForLabels, percentageIncreasement, dividerForScaleAdjustmentBasis, extraWidthPaddingScale, selectedTemplate, stageRef, onDownloadCanvas, windowTemplates, dividerForScaleAdjustment, heightScaleFactor, widthScaleFactor, masterScaleFactor, layerConfig, transformDimension, increasementForWiderWidths, calculateStageConfigHeight, setElementsInitialPositions, setTemplatePositionsAndStageConfig, setTemplatesPositionsAndStageConfig, getCircleLetters, selectWindowTemplate, setSelectedTemplateDimensions, moveWindowsToTop, recalculateCanvas, sortedWindowElements, CombinationsWarning }
}

})